import React, {Component, Fragment} from 'react';
import PropTypes from 'prop-types';
import axios from 'axios';
import {connect} from 'react-redux';
import {compose} from 'recompose';
import ReactTable from 'react-table';
import {CSVLink} from 'react-csv';
import ReactToPrint from 'react-to-print';
import {RESOURCES as reportsResources} from '../fuseReportsResourceMap';
import {
    convertDate,
    convertDateTo24HourString,
    sortCurrency,
    toCurrency
} from '../shared/functions';
import {getGetHeaders, getPostHeaders} from '../shared/serverFunctions';
import {setDefaultTableSort, getTableState, filterCaseInsensitive} from '../shared/tableFunctions';
import {checkTokenStatus} from '../shared/tokenFunctions';
import DialogPaymentSeq from './DialogPaymentSeq';
import DialogSaleReceipt from './DialogSaleReceipt';
import DialogSaleSeq from './DialogSaleSeq';
import Config from '../messages';
import FuseTheme from '../global/theme';
import {commonStyles} from '../shared/styles';
import {tableStyles} from '../shared/styles-table';
import '../shared/react-table.css';
import CircularProgress from 'material-ui/CircularProgress';
import Dialog from 'material-ui/Dialog';
import RaisedButton from 'material-ui/RaisedButton';
import FlatButton from "material-ui/FlatButton";
import ExportIcon from 'material-ui/svg-icons/navigation/arrow-downward';
import TableIcon from 'material-ui/svg-icons/action/credit-card';

const styles = {
    headerButton: {
        display: 'inline-block',
        color: FuseTheme.palette.alternateTextColor,
        margin: '0 10px 0 0',
    },
    headerButtonIcon: {
        display: 'inline-block',
        color: FuseTheme.palette.alternateTextColor,
        margin: '0 6px 0 8px',
        verticalAlign: 'middle',
    },
    headerButtonLink: {
        display: 'inline-block',
        fontWeight: 500,
        color: FuseTheme.palette.alternateTextColor,
        textTransform: 'uppercase',
        textDecoration: 'none',
        margin: '0 12px 0 0'
    },
    summaryIcon: {
        fill: FuseTheme.palette.accent2Color,
        width: 36,
        height: 36,
        margin: '0 10px 0 0',
        verticalAlign: 'middle'
    },
    tdMax: {width: 90, maxWidth: 90},
    left: {textAlign: 'left'},
    center: {textAlign: 'center'},
    right: {textAlign: 'right'},
    wrapText: {wordWrap: 'break-word'},
    hide: {display: 'none'},
    printBtn: {fontWeight: 'bold', cursor: 'pointer'},
    button: {color: FuseTheme.palette.primary3Color},
    linkDefault: {
        cursor: 'pointer',
        color: FuseTheme.palette.primary1Color
    },
    dialogContent: {maxWidth: '70%'},
    flexContainerRight: {
        display: 'flex',
        justifyContent: 'flex-end',
    }
};

/**
 * Class for retrieving and displaying Credit Card Transaction Summary in a single table
 *
 */
class TransactionLogReport extends Component {
    constructor(props) {
        super(props);
        this.state = {
            summaryLoading: false,
            reportName: 'Credit Card Transactions',
            summaryComplete: false,
            summaryList: [],
            summaryTableData: [],
            summaryTableHeaders: [
                {label: 'Payment Id', key: 'payment_seq'},
                {label: 'Sale Id', key: 'sale_seq'},
                {label: 'Transaction Date', key: 'transaction_date_string'},
                {label: 'Register ID', key: 'register_id'},
                {label: 'Currency', key: 'currency'},
                {label: 'Price', key: 'subtotal'},
                {label: 'Tax', key: 'tax'},
                {label: 'Card Type', key: 'card_type'},
                {label: 'Last 4', key: 'card_last4'},
                {label: 'Card Data Source', key: 'card_data_source'},
                {label: 'PN Ref', key: 'pn_ref'},
                {label: 'Auth', key: 'authcode'},
                {label: 'Mode', key: 'mode'},
            ],
            message: null,
            transactionData: null,
            transactionDialogOpen: false,
            creditList: [],
            creditTableData: [],
            saleTransactionData: null,
            saleDialogOpen: false,
            salePaymentsList: [],
            saleCreditsList: [],
            saleProductList: [],
            saleProductReturnedList: [],
            saleDiscountsList: [],
        };

        this.handleExportClick = this.handleExportClick.bind(this);
        this.handleSortOrFilterChange = this.handleSortOrFilterChange.bind(this);
    }

    componentWillMount() {
        this.fetchTransactionsData();
    }

    componentWillReceiveProps(nextProps) {
        let clearTable = false;
        if (
            nextProps.startDate !== this.props.startDate ||
            nextProps.endDate !== this.props.endDate ||
            nextProps.offset !== this.props.offset ||
            nextProps.lastFour !== this.props.lastFour ||
            nextProps.amount !== this.props.amount
        ) {
            clearTable = true;
        }
        if (clearTable) {
            this.setState({
                summaryComplete: false,
                summaryLoading: false,
                summaryList: [],
                summaryTableData: []
            });
        }
    }

    componentDidUpdate(prevProps) {
        if (
            prevProps.startDate !== this.props.startDate ||
            prevProps.endDate !== this.props.endDate ||
            prevProps.offset !== this.props.offset ||
            prevProps.lastFour !== this.props.lastFour ||
            prevProps.amount !== this.props.amount
        ) {
            this.fetchTransactionsData();
        }
    }

    /* API Calls ------------------------------ */

    /**
     * Get Credit Card Sales Transaction data for a specific date range
     */
    fetchTransactionsData() {
        // Convert amount based on whether user submitted currency format
        let amount = (this.props.amount) ? this.props.amount : null;
        let currency = [];
        if (amount) {
            if (amount.indexOf('.') !== -1) {
                amount = amount.replace('.', '');
            } else {
                amount = (amount * 100).toString();
            }
            currency = [amount];
        }
        // Assemble the URI
        const params = {
            creditCard: {
                card_last4: [this.props.lastFour],
                currency: currency
            },
            dateRange: {
                fromDay: this.props.startDate,
                toDay: this.props.endDate,
                tz: this.props.offset
            }
        };

        let resource = reportsResources.CreditCardTransactions.POST(params);

        const tokenStatus = checkTokenStatus(null);
        if (tokenStatus.expired) {
            return;
        }

        axios({
            method: resource.method,
            url: resource.uri,
            headers: getPostHeaders(),
            data: params
        }).then((result) => {
            if (result.data && result.data.payload) {
                this.processTransactionsData(result.data.payload);
            }
        }).catch((error) => {
            let message = Config.fuse.error.error + this.state.reportName + '.';
            if (error.response) {
                // Server response outside 2xx
                if (error.response.data && error.response.data.error) {
                    message = Config.fuse.error.error + this.state.reportName + ': ' + error.response.data.error;
                }
            } else if (error.request) {
                // No response
                message = Config.fuse.error.no_response + this.state.reportName + '.';
            } else {
                console.log('Error', error.message);
            }
            this.setState({
                summaryList: [],
                summaryTableData: [],
                summaryComplete: false,
            }, () => {
                if (typeof this.props.onError === 'function') {
                    this.props.onError(message);
                }
            });
        });
    }

    /**
     * Fetch Transaction Data for dialog
     *
     * @param seq
     * @param type
     */
    fetchDialogTransactionData(seq, type) {

        const tokenStatus = checkTokenStatus(null);
        if (tokenStatus.expired) {
            return;
        }

        let resource = reportsResources.TransactionLogPaymentBySeq.GET_LIST(seq);
        axios({
            method: 'get',
            url: resource.uri,
            headers: getGetHeaders()
        }).then((result) => {
            if (result.data && result.data.payload) {
                this.processDialogTransactionData(type, result.data.payload);
            }
        }).catch((error) => {
            if (error.response) {
                // Server response outside 2xx
                console.log(error.response.data);
            } else if (error.request) {
                // No response
                console.log(error.request);
            } else {
                console.log('Error', error.message);
            }
        });
    }

    /**
     * Fetch Sale Data for Dialog
     *
     * @param seq
     * @param type
     */
    fetchDialogSaleTransactionData(seq, type) {

        const tokenStatus = checkTokenStatus(null);
        if (tokenStatus.expired) {
            return;
        }

        let resource = reportsResources.SaleLogBySeq.GET_LIST(seq);

        axios({
            method: 'get',
            url: resource.uri,
            headers: getGetHeaders()
        }).then((result) => {
            if (result.data && result.data.payload) {
                this.processDialogSaleTransactionData(type, result.data.payload);
            }
        }).catch((error) => {
            if (error.response) {
                // Server response outside 2xx
                console.log(error.response.data);
            } else if (error.request) {
                // No response
                console.log(error.request);
            } else {
                console.log('Error', error.message);
            }
        });
    }

    /* Data Processing ------------------------------ */

    /**
     * Store transaction results
     *
     * @param data
     */
    processTransactionsData(data) {
        const emptyCurrency = toCurrency(0);
        let summaryList = [];
        if (data.aggregations) {
            if (data.aggregations.by_payment_seq && data.aggregations.by_payment_seq.buckets) {
                let results = data.aggregations.by_payment_seq.buckets;
                let totalResults = results.length;
                for (let s = 0; s < totalResults; s++) {
                    let result = results[s];
                    let details = result.column_name.hits.hits[0]._source.rxtxMsg.payload;
                    let tDate = (details.transaction_date) ? details.transaction_date : 'No Date';
                    let tDateString = (details.transaction_date) ? convertDate(details.transaction_date, 'display', true, true, this.props.offset, true) : 'No Date';
                    let tDate24HString = (details.transaction_date) ? convertDateTo24HourString(details.transaction_date, true, this.props.offset) : 'No Date';
                    let saleTax = (details.tax) ? Number(details.tax) : 0;
                    let additionalTax = (details.additional_tax_total) ? Number(details.additional_tax_total) : 0;
                    let totalTaxes = saleTax + additionalTax;
                    let detailRow = {
                        location: (details.client.location) ? details.client.location : '----',
                        payment_seq: (details.payment_seq) ? details.payment_seq : '----',
                        sale_seq: (details.sale_seq) ? details.sale_seq : '----',
                        transaction_date: tDate,
                        transaction_date_string: tDateString,
                        transaction_date_24h_string: tDate24HString,
                        transaction_type: (details.transaction_type) ? details.transaction_type : '----',
                        transaction: (details.transaction) ? details.transaction : '----',
                        currency: (details.currency) ? (toCurrency(details.currency / 100)) : emptyCurrency,
                        subtotal: (details.subtotal) ? (toCurrency(details.subtotal / 100)) : emptyCurrency,
                        tax: (totalTaxes) ? (toCurrency(totalTaxes / 100)) : emptyCurrency,
                        card_type: (details.card_type) ? details.card_type : '----',
                        card_last4: (details.card_last4) ? details.card_last4 : '----',
                        card_data_source: (details.card_data_source) ? details.card_data_source : '----',
                        pn_ref: (details.pn_ref) ? details.pn_ref : '----',
                        authcode: (details.authcode) ? details.authcode : '----',
                        mode: (details.mode) ? details.mode : '----',
                        register_id: (details.client && details.client.register) ? details.client.register : '--'
                    };
                    summaryList.push(detailRow);
                }
            }
        }

        this.setState({
            summaryList: summaryList,
            summaryLoading: false,
            summaryTableData: summaryList,
            summaryComplete: true,
        }, () => {
            if (typeof this.props.onSummaryComplete === 'function') {
                this.props.onSummaryComplete(true);
            }
        });
    }

    processDialogTransactionData(type, data) {
        const emptyCurrency = toCurrency(0);
        let creditList = [];

        if (data.hits.hits[0]) {

            for (let c = 0; c < data.hits.hits.length; c++) {
                let details = data.hits.hits[c]._source.rxtxMsg.payload;
                let tDateString = (details.transaction_date) ? convertDate(details.transaction_date, 'display', true, true, this.props.offset, true) : 'No Date';
                let tDate24HString = (details.transaction_date) ? convertDateTo24HourString(details.transaction_date, true, this.props.offset) : 'No Date';

                if (details.transaction === 'payment') {
                    let salesTax = (details.tax) ? Number(details.tax) : 0;
                    let additionalTax = (details.additional_tax_total) ? Number(details.additional_tax_total) : 0;
                    let totalTaxes = salesTax + additionalTax;
                    const detailRow = {
                        location: (details.client.location) ? details.client.location : null,
                        register: (details.client.register) ? details.client.register : null,
                        currency: (details.currency) ? toCurrency(details.currency / 100) : emptyCurrency,
                        mode: (details.mode) ? details.mode : '----',
                        payment_seq: (details.payment_seq) ? details.payment_seq : null,
                        sale_seq: (details.sale_seq) ? details.sale_seq : null,
                        subtotal: (details.subtotal) ? toCurrency(details.subtotal / 100) : emptyCurrency,
                        tax: (totalTaxes) ? toCurrency(totalTaxes / 100) : emptyCurrency,
                        transaction: (details.transaction) ? details.transaction : null,
                        transaction_date: (details.transaction_date) ? details.transaction_date : 'No Date',
                        transaction_date_string: tDateString,
                        transaction_date_24h_string: tDate24HString,
                        transaction_type: (details.transaction_type) ? details.transaction_type : '----',
                        authcode: (details.authcode) ? details.authcode : null,
                        card_data_source: (details.card_data_source) ? details.card_data_source : null,
                        card_first_name: (details.card_first_name) ? details.card_first_name : null,
                        card_last_name: (details.card_last_name) ? details.card_last_name : null,
                        card_last4: (details.card_last4) ? details.card_last4 : null,
                        card_status: (details.card_status) ? 'True' : null,
                        card_type: (details.card_type) ? details.card_type : null,
                        pn_ref: (details.pn_ref) ? details.pn_ref : null,
                        message: (details.message) ? details.message : null,
                        response_message: (details.response_message) ? details.response_message : null,
                        code: (details.code) ? details.code : null,
                        ext_data: (details.ext_data) ? details.ext_data : null
                    };

                    this.setState({
                        transactionData: detailRow,
                    });

                } else {
                    let subtotal = parseFloat(toCurrency(details.subtotal / 100));
                    let tax = parseFloat(toCurrency(details.tax / 100));
                    let amount = (subtotal + tax).toFixed(2);
                    let salesTax = (details.tax) ? Number(details.tax) : 0;
                    let additionalTax = (details.additional_tax_total) ? Number(details.additional_tax_total) : 0;
                    let totalTaxes = salesTax + additionalTax;
                    const creditRow = {
                        id: (details.credit_seq) ? details.credit_seq : '----',
                        currency: (details.currency) ? toCurrency(details.currency / 100) : emptyCurrency,
                        location: (details.client.location) ? details.client.location : '----',
                        register: (details.client.register) ? details.client.register : '----',
                        amount: (amount) ? amount : emptyCurrency,
                        subtotal: (details.subtotal) ? toCurrency(details.subtotal / 100) : emptyCurrency,
                        tax: (totalTaxes) ? toCurrency(totalTaxes / 100) : emptyCurrency,
                        mode: (details.mode) ? details.mode : '----',
                        transaction_date: (details.transaction_date) ? details.transaction_date : 'No Date',
                        transaction_date_string: tDateString,
                        transaction_date_24h_string: tDate24HString
                    };

                    creditList.push(creditRow);

                    this.setState({
                        creditList: creditList,
                        creditTableData: creditList
                    });
                }
            }
        }
    }

    processDialogSaleTransactionData(type, data) {
        const emptyCurrency = toCurrency(0);
        let salePaymentsList = [];
        let saleCreditsList = [];
        let saleProductList = [];
        let saleProductReturnedList = [];
        let saleDiscountsList = [];
        let divisor = 100;

        let details = data.hits.hits[0]._source.rxtxMsg.payload;
        let tDateString = (details.transaction_date) ? convertDate(details.transaction_date, 'display', true, true, this.props.offset, true) : 'No Date';
        let tDate24HString = (details.transaction_date) ? convertDateTo24HourString(details.transaction_date, true, this.props.offset) : 'No Date';

        if (details) {
            const detailRow = {
                dba_email: (details.location && details.location.dba) ? details.location.dba.email : '',
                dba_name: (details.location && details.location.dba) ? details.location.dba.name : '',
                device: (details.client.device) ? details.client.device : '----',
                initial_net: (details.initial_net) ? (toCurrency(details.initial_net / divisor)) : emptyCurrency,
                initial_tax: (details.initial_tax) ? (toCurrency(details.initial_tax / divisor)) : emptyCurrency,
                initial_additional_taxes: (details.initial_additional_taxes) ? (toCurrency(details.initial_additional_taxes / divisor)) : emptyCurrency,
                initial_total: (details.initial_total) ? (toCurrency(details.initial_total / divisor)) : emptyCurrency,
                location: (details.client.location) ? details.client.location : '----',
                location_address: (details.location.address) ? details.location.address : '',
                location_city: (details.location.city) ? details.location.city : '',
                location_telephone: (details.location.telephone) ? details.location.telephone : '',
                location_state: (details.location.state) ? details.location.state : '',
                location_tax_rate: (details.location.tax_rate) ? details.location.tax_rate.toString() : '0',
                location_zipcode: (details.location.zipcode) ? details.location.zipcode : '',
                loyalty_id: (details.loyalty_customer && details.loyalty_customer.id) ? details.loyalty_customer.id : '',
                register: (details.client.register) ? details.client.register : '----',
                user: (details.client.user) ? details.client.user : '----',
                version: (details.client.version) ? details.client.version : '----',
                mode: (details.mode) ? details.mode : '----',
                seq: (details.seq) ? details.seq : '----',
                transaction_date_string: tDateString,
                transaction_date_24h_string: tDate24HString
            };

            if (details.payments) {
                let totalPayments = details.payments.length;
                for (let i = 0; i < totalPayments; i++) {
                    let paymentResult = details.payments[i];
                    const paymentRow = {
                        seq: (paymentResult.seq) ? paymentResult.seq : '----',
                        type: (paymentResult.type) ? paymentResult.type : '----',
                        card_type: (paymentResult.card_type) ? paymentResult.card_type : '----',
                        card_last4: (paymentResult.card_last4) ? paymentResult.card_last4 : '----',
                        card_data_source: (paymentResult.card_data_source) ? paymentResult.card_data_source : '----',
                        pn_ref: (paymentResult.pn_ref) ? paymentResult.pn_ref : '----',
                        authcode: (paymentResult.authcode) ? paymentResult.authcode : '----',
                        mode: (paymentResult.mode) ? paymentResult.mode : '----',
                        subtotal: (paymentResult.subtotal) ? (toCurrency(paymentResult.subtotal / divisor)) : emptyCurrency,
                        tax: (paymentResult.tax) ? (toCurrency(paymentResult.tax / divisor)) : emptyCurrency,
                        additional_tax_total: (paymentResult.additional_tax_total) ? (toCurrency(paymentResult.additional_tax_total / divisor)) : emptyCurrency,
                        additional_taxes: (paymentResult.additional_taxes) ? paymentResult.additional_taxes : null,
                        currency: (paymentResult.currency)
                            ? (paymentResult.type === 'loyalty_points')
                                ? (toCurrency(paymentResult.currency / 1000))
                                : (toCurrency(paymentResult.currency / divisor))
                            : emptyCurrency
                    };
                    salePaymentsList.push(paymentRow);
                }
                // Order payments: Loyalty Points, Cash, Credit
                const paymentSort = ['loyalty_points', 'cash', 'out_of_scope'];
                let paymentObj = {};
                paymentSort.forEach(function (a, i) {
                    paymentObj[a] = i + 1;
                });
                salePaymentsList.sort(function (a, b) {
                    return (paymentObj[a.type] || Infinity) - (paymentObj[b.type] || Infinity);
                });
                this.setState({
                    salePaymentsList: salePaymentsList
                });
            }

            if (details.credits) {
                let totalCredits = details.credits.length;
                for (let i = 0; i < totalCredits; i++) {
                    let creditResult = details.credits[i];
                    let tDateString = (creditResult.credit_date) ? convertDate(creditResult.credit_date, 'display', true, true, this.props.offset, true) : 'No Date';
                    const creditRow = {
                        seq: (creditResult.seq) ? creditResult.seq : '----',
                        payment_seq: (creditResult.payment_seq) ? creditResult.payment_seq : '----',
                        type: (creditResult.type) ? creditResult.type : '----',
                        credit_date: tDateString,
                        subtotal: (creditResult.subtotal) ? (toCurrency(creditResult.subtotal / 100)) : emptyCurrency,
                        tax: (creditResult.tax) ? (toCurrency(creditResult.tax / 100)) : emptyCurrency,
                        additional_tax_total: (creditResult.additional_tax_total) ? (toCurrency(creditResult.additional_tax_total / divisor)) : emptyCurrency,
                        currency: (creditResult.currency) ? (toCurrency(creditResult.currency / 100)) : emptyCurrency,
                        card_type: (creditResult.card_type) ? creditResult.card_type : '----',
                        card_last4: (creditResult.card_last4) ? creditResult.card_last4 : '----',
                        card_data_source: (creditResult.card_data_source) ? creditResult.card_data_source : '----',
                        pn_ref: (creditResult.pn_ref) ? creditResult.pn_ref : '----',
                        authcode: (creditResult.authcode) ? creditResult.authcode : '----',
                        mode: (creditResult.mode) ? creditResult.mode : '----',
                        status: (creditResult.status) ? "True" : '----'

                    };
                    saleCreditsList.push(creditRow);
                }
                this.setState({
                    saleCreditsList: saleCreditsList
                });
            }

            let discountTotal = 0;
            if (details.discounts) {
                let totalDiscounts = details.discounts.length;
                for (let i = 0; i < totalDiscounts; i++) {
                    let discountResult = details.discounts[i];
                    const discountRow = {
                        code: (discountResult.code) ? discountResult.code : '----',
                        name: (discountResult.name) ? discountResult.name : '----',
                        discount_each: (discountResult.discount_each) ? (toCurrency(discountResult.discount_each / 100)) : emptyCurrency
                    };
                    saleDiscountsList.push(discountRow);
                    discountTotal += (discountResult.discount_each) ? parseInt(discountResult.discount_each, 10) : 0;
                }
                this.setState({
                    saleDiscountsList: saleDiscountsList
                });
            }

            if (details.product_units) {
                let totalProducts = details.product_units.length;
                for (let i = 0; i < totalProducts; i++) {
                    let productResult = details.product_units[i];
                    // Use Display Price until calculations are adjusted
                    let productQty = (productResult.qty) ? productResult.qty : 0;
                    let displayPrice = (productResult.display_price) ? productResult.display_price : 0;
                    let displaySubtotal = (displayPrice * productQty);
                    const productRow = {
                        product_name: (productResult.product_name) ? productResult.product_name : '----',
                        product_unit_id: (productResult.product_unit_id) ? productResult.product_unit_id : '----',
                        product_unit_type: (productResult.product_unit_type) ? productResult.product_unit_type : '----',
                        qty: (productResult.qty) ? productResult.qty : '----',
                        price_each: (productResult.price_each) ? (toCurrency(productResult.price_each / divisor)) : emptyCurrency,
                        price_each_display: (displayPrice) ? toCurrency(displayPrice) : emptyCurrency,
                        price_subtotal: (productResult.price_subtotal) ? (toCurrency(productResult.price_subtotal / divisor)) : emptyCurrency,
                        price_subtotal_display: (displaySubtotal) ? toCurrency(displaySubtotal) : emptyCurrency,
                        price_total: (productResult.price_total) ? (toCurrency(productResult.price_total / divisor)) : emptyCurrency,
                    };
                    saleProductList.push(productRow);
                }
                this.setState({
                    saleProductList: saleProductList
                });
            }

            if (details.product_units_returned) {
                let totalProductsReturned = details.product_units_returned.length;
                for (let i = 0; i < totalProductsReturned; i++) {
                    let productReturnedResult = details.product_units_returned[i];
                    const productReturnedRow = {
                        product_unit_id: (productReturnedResult.product_unit_id) ? productReturnedResult.product_unit_id : '----',
                        product_name: (productReturnedResult.product_name) ? productReturnedResult.product_name : '----',
                        product_unit_type: (productReturnedResult.product_unit_type) ? productReturnedResult.product_unit_type : '----',
                        qty: (productReturnedResult.qty) ? productReturnedResult.qty : '----',
                    };
                    saleProductReturnedList.push(productReturnedRow);
                }
                this.setState({
                    saleProductReturnedList: saleProductReturnedList
                });
            }

            detailRow.discount_total = (discountTotal) ? (toCurrency(discountTotal / divisor)) : emptyCurrency;
            detailRow.discount_total_string = detailRow.discount_total;
            if (discountTotal < 0) {
                const discountAmount = discountTotal.toString().substr(1);
                detailRow.discount_total_string = '-$' + toCurrency(parseInt(discountAmount, 10) / 100);
            }
            this.setState({
                saleTransactionData: detailRow
            });
        }
    }

    /* Table Building ------------------------------ */

    /**
     * Update selected table data based on sort
     *
     * @param event
     * @param table
     */
    handleSortOrFilterChange(event, table) {
        const tableData = getTableState(table);
        if (tableData && tableData.length > 0) {
            this.setState({
                summaryTableData: tableData
            });
        }
    }

    /**
     * Open/Close Dialog
     * @param e
     * @param seq
     * @param type
     */
    handleOpen = (e, seq, type) => {
        this.setState({
                transactionDialogOpen: true
            },
            () => {
                this.fetchDialogTransactionData(seq, type);
            });
    };

    handleSaleOpen = (e, seq, type) => {
        this.setState({
                saleDialogOpen: true
            },
            () => {
                this.fetchDialogSaleTransactionData(seq, type);
            });
    };

    handleClose = () => {
        this.setState({
            transactionData: null,
            transactionDialogOpen: false,
            saleDialogOpen: false,
            saleTransactionData: null
        });
    };

    /**
     * Handle event to retrieve current state of table data
     *
     * @param event
     */
    handleExportClick = (event) => {
        // CSVLink handles generation and export of CSV
    };

    /**
     * Convert Start and End Dates to range for Export filename
     *
     * @param startDate
     * @param endDate
     * @return {string}
     */
    convertDatesForFile(startDate, endDate) {
        let from = new Date(startDate + 'T00:00:00');
        let to = new Date(endDate + 'T00:00:00');
        let fileStr = '';
        let yy = from.getFullYear();
        let mo = String('00' + parseInt(from.getMonth() + 1, 10)).slice(-2);
        let dd = String('00' + from.getDate()).slice(-2);
        fileStr += yy.toString() + mo.toString() + dd.toString() + '-';
        yy = to.getFullYear();
        mo = String('00' + parseInt(to.getMonth() + 1, 10)).slice(-2);
        dd = String('00' + to.getDate()).slice(-2);
        fileStr += yy.toString() + mo.toString() + dd.toString();
        return fileStr;
    }

    /* Table Display ------------------------------ */

    render() {

        const {
            amount,
            lastFour,
            endDate,
            startDate,
            system,
            includeReceipt
        } = this.props;

        const {
            summaryList,
            summaryTableData,
            summaryTableHeaders,
            summaryComplete,
            summaryLoading,
            transactionData,
            transactionDialogOpen,
            creditList,
            creditTableData,
            saleDialogOpen,
            saleTransactionData,
            salePaymentsList,
            saleCreditsList,
            saleProductList,
            saleProductReturnedList,
            saleDiscountsList,
        } = this.state;

        const amountCurrency = (amount && amount !== '') ? '$' + toCurrency(amount, false) : '(any)';
        const dateString = this.convertDatesForFile(startDate, endDate);
        const defaultTransactionsSort = setDefaultTableSort({field: 'transaction_date', order: 'DESC'});

        // ReactTable headers and columns for Table
        const tableTransactionsColumns = [
            {
                Header: '',
                columns: [
                    {
                        Header: 'Payment Id',
                        accessor: 'payment_seq',
                        style: tableStyles.wrapText,
                        Cell: props => (
                            <a style={styles.linkDefault}
                               onClick={(e) => this.handleOpen(e, props.row.payment_seq, props.row.transaction)}
                            >
                                {props.row.payment_seq}
                            </a>
                        )
                    },
                    {
                        Header: 'Sale Id',
                        accessor: 'sale_seq',
                        style: tableStyles.wrapText,
                        Cell: props => (
                            <a style={styles.linkDefault}
                               onClick={(e) => this.handleSaleOpen(e, props.row.sale_seq, props.row.transaction)}
                            >
                                {props.row.sale_seq}
                            </a>
                        )
                    },
                    {
                        Header: 'Transaction Date',
                        accessor: 'transaction_date',
                        Cell: props => (
                            <span>{props.row.transaction_date_string}</span>
                        )
                    },
                    {
                        Header: '',
                        accessor: 'transaction_date_string',
                        style: tableStyles.hide,
                        headerStyle: tableStyles.hide
                    },
                    {
                        Header: 'Location',
                        accessor: 'location',
                        style: tableStyles.tdMax
                    },
                    {
                        Header: 'Register',
                        accessor: 'register_id',
                        style: tableStyles.tdMax
                    },
                    {
                        Header: 'Currency',
                        accessor: 'currency',
                        sortMethod: (a, b) => sortCurrency(a, b),
                        filterable: false,
                        style: tableStyles.right
                    },
                    {
                        Header: 'Price',
                        accessor: 'subtotal',
                        sortMethod: (a, b) => sortCurrency(a, b),
                        filterable: false,
                        style: tableStyles.right
                    },
                    {
                        Header: 'Tax',
                        accessor: 'tax',
                        sortMethod: (a, b) => sortCurrency(a, b),
                        filterable: false,
                        style: tableStyles.right
                    },
                    {
                        Header: 'Card Type',
                        accessor: 'card_type',
                    },
                    {
                        Header: 'Card Data Source',
                        accessor: 'card_data_source'
                    },
                    {
                        Header: 'PN Ref',
                        accessor: 'pn_ref',
                    },
                    {
                        Header: 'Auth',
                        accessor: 'authcode',
                        style: tableStyles.tdMax,
                    },
                    {
                        Header: 'Mode',
                        accessor: 'mode',
                        style: tableStyles.tdMax,
                    }
                ]
            }
        ];

        // Dialog Actions
        const dialogActions = [
            <FlatButton
                label="Close"
                primary={true}
                onClick={this.handleClose}
            />
        ];

        return (
            <div>
                {summaryLoading && !summaryComplete &&
                    <div style={commonStyles.loaderContainer}>
                        <h4>Generating Report...</h4>
                        <CircularProgress
                            size={24}
                            thickness={2}
                            style={commonStyles.loader}
                        />
                    </div>
                }
                {summaryComplete &&
                    <div className="reports-table-container">

                        {/* Table Header: Credit Card Transactions */}
                        <div className="reports-table-title table-table-header">
                            <div className="btn-report-container report-header-button-container">
                                {/* Export Credit Card Transactions */}
                                <FlatButton
                                    primary
                                    type="button"
                                    label=""
                                    style={commonStyles.headerButton}
                                >
                                    <ExportIcon style={commonStyles.headerButtonIcon}/>
                                    <CSVLink
                                        data={summaryTableData}
                                        headers={summaryTableHeaders}
                                        filename={'Credit-Card-Transactions-Report-' + system + '-' + dateString + '.csv'}
                                        onClick={(e) => this.handleExportClick(e)}
                                        target="_blank"
                                        style={commonStyles.headerButtonLink}
                                    >
                                        Export Credit Card Transactions Report
                                    </CSVLink>
                                </FlatButton>
                            </div>
                            <h3 className="table-table-header-title">
                                <TableIcon style={commonStyles.summaryIcon}/>
                                <span>Credit Card Transactions</span>
                            </h3>
                            <div style={commonStyles.tableDetail}>
                                Credit Card Last 4: {lastFour}<br/>
                                Amount: {amountCurrency}<br/>
                            </div>
                        </div>

                        {/* Table: Credit Card Transactions */}
                        <ReactTable
                            key="table-credit-card-transactions"
                            data={summaryList}
                            columns={tableTransactionsColumns}
                            defaultPageSize={10}
                            showPagination={true}
                            defaultSorted={defaultTransactionsSort}
                            filterable
                            defaultFilterMethod={filterCaseInsensitive}
                            onSortedChange={(e) => this.handleSortOrFilterChange(e, this.summaryReactTable)}
                            onFilteredChange={(e) => this.handleSortOrFilterChange(e, this.summaryReactTable)}
                            ref={(r) => this.summaryReactTable = r}
                            resizable={false}
                            minRows={1}
                            className="-striped overflow no-print"
                        />
                        <ReactTable
                            key="table-credit-card-transactions-print"
                            data={summaryTableData}
                            columns={tableTransactionsColumns}
                            defaultPageSize={10000}
                            showPagination={false}
                            defaultSorted={defaultTransactionsSort}
                            defaultSortDesc={true}
                            filterable
                            resizable={false}
                            minRows={1}
                            className="print-only"
                        />
                        <Dialog
                            modal={false}
                            open={transactionDialogOpen}
                            onRequestClose={this.handleClose}
                            contentClassName={"dialogContentTable"}
                            actionsContainerClassName={"dialogPrintOnly"}
                            autoScrollBodyContent={true}
                            actions={dialogActions}
                        >
                            <div style={tableStyles.flexContainerRight}>
                                <ReactToPrint
                                    trigger={() =>
                                        <RaisedButton
                                            primary
                                            type="button"
                                            label="Print Report"
                                            style={tableStyles.printBtn}/>
                                    }
                                    content={() => this.printReportRef}
                                />
                            </div>
                            {!transactionData &&
                                <div className="loader-container">
                                    <h4>Generating Payment Log...</h4>
                                    <CircularProgress
                                        size={24}
                                        thickness={2}
                                        className="loader"
                                    />
                                </div>
                            }
                            {transactionData &&
                                <DialogPaymentSeq
                                    ref={el => (this.printReportRef = el)}
                                    data={transactionData}
                                    creditList={creditList}
                                    creditTableData={creditTableData}
                                    handleSortOrFilterChange={this.handleSortOrFilterChange}
                                />
                            }
                        </Dialog>
                        <Dialog
                            modal={false}
                            open={saleDialogOpen}
                            onRequestClose={this.handleClose}
                            contentClassName={"dialogContentTable"}
                            actionsContainerClassName={"dialogPrintOnly"}
                            autoScrollBodyContent={true}
                            actions={dialogActions}
                        >
                            <div style={tableStyles.flexContainerRight}>
                                {includeReceipt &&
                                    <ReactToPrint
                                        trigger={() =>
                                            <RaisedButton
                                                primary
                                                type="button"
                                                label="Print Receipt"
                                                style={tableStyles.printBtn}
                                            />
                                        }
                                        content={() => this.printReceiptRef}
                                    />
                                }
                                <ReactToPrint
                                    trigger={() =>
                                        <RaisedButton
                                            primary
                                            type="button"
                                            label="Print Report"
                                            style={tableStyles.printBtn}
                                        />
                                    }
                                    content={() => this.printReportRef}
                                />
                            </div>
                            {!saleTransactionData &&
                                <div className="loader-container">
                                    <h4>Generating Sale Log...</h4>
                                    <CircularProgress
                                        size={24}
                                        thickness={2}
                                        className="loader"
                                    />
                                </div>
                            }
                            {saleTransactionData &&
                                <Fragment>
                                    <DialogSaleSeq
                                        ref={e => (this.printReportRef = e)}
                                        data={saleTransactionData}
                                        payments={salePaymentsList}
                                        credits={saleCreditsList}
                                        products={saleProductList}
                                        returns={saleProductReturnedList}
                                        discounts={saleDiscountsList}
                                        handleSortOrFilterChange={this.handleSortOrFilterChange}
                                    />
                                    {includeReceipt &&
                                        <Fragment>
                                            <DialogSaleReceipt
                                                ref={e => (this.printReceiptRef = e)}
                                                data={saleTransactionData}
                                                payments={salePaymentsList}
                                                products={saleProductList}
                                                discounts={saleDiscountsList}
                                            />
                                            <div style={tableStyles.center}>
                                                <ReactToPrint
                                                    trigger={() =>
                                                        <RaisedButton
                                                            primary
                                                            type="button"
                                                            label="Print Receipt"
                                                            style={tableStyles.printBtnCenter}/>
                                                    }
                                                    content={() => this.printReceiptRef}
                                                />
                                            </div>
                                        </Fragment>
                                    }
                                </Fragment>
                            }
                        </Dialog>
                    </div>
                }
            </div>
        )
    }
}

TransactionLogReport.propTypes = {
    addField: PropTypes.bool,
    amount: PropTypes.string,
    endDate: PropTypes.string,
    includeReceipt: PropTypes.bool,
    lastFour: PropTypes.string,
    offset: PropTypes.string.isRequired,
    onDisplayMessage: PropTypes.func,
    onError: PropTypes.func,
    onSummaryComplete: PropTypes.func,
    startDate: PropTypes.string,
    system: PropTypes.string,
};

TransactionLogReport.defaultProps = {
    addField: true,
    amount: null,
    endDate: null,
    includeReceipt: true,
    lastFour: null,
    offset: null,
    startDate: null,
};

const enhance = compose(
    connect()
);

export default enhance(TransactionLogReport);



// WEBPACK FOOTER //
// ./src/Fuse/components/CreditCardTransactionReport.js